import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Spin } from 'antd';
import styled from 'styled-components';

import { CareerIcon, SkillIcon, TrackIcon } from 'atoms';
import { Colors } from 'context';
import { useProfessions } from 'core';
import { IProfessionSkill } from 'core/hooks/Growth/transform/transformProfessions';
import {
  progressSources,
  RequirementStatus,
} from 'core/hooks/Growth/transform/transformSkillProgress';
import { useCareerJourney } from 'core/hooks/CareerJourney/useCareerJourney';
import { CourseAccess, useProfessionCourse } from 'core/hooks/Growth/useProfessionCourse';
import { CareerBackLink } from 'molecules/CareerBackLink';
import { CareerLinkCard } from 'molecules/CareerLinkCard';
import { useChooseGoalDialog } from 'organisms/CareerChooseGoalDialog';
import { skillMapHref } from 'router/careerLinks';
import { PATHS } from 'router/config';
import { careerColor, CTA_TEXT } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

/**
 * Страница одной профессии (ТЗ 0039, доводка UX).
 *
 * Владелец: «отдельные страницы профессии с call to action, разными
 * информацией: зачем это всё нужно, какие знания нужны для каждой карьеры,
 * насколько ты к ней подходишь».
 *
 * Порядок блоков отвечает ровно на это:
 *   1. чем этот человек занимается (длинный текст, а не строка из списка);
 *   2. НАСКОЛЬКО я подхожу — кольцо и разбивка «есть / не хватает»;
 *   3. каждый навык кликабелен и ведёт на свою страницу;
 *   4. какие НАШИ курсы туда ведут — это и есть призыв к действию.
 *
 * ⚠️ Данные берутся из общего careerProfessions, а не из отдельного запроса
 * на профессию: список короткий (пять-шесть треков), и второй источник тех же
 * чисел разъехался бы с первым.
 */
/** Кнопка курса по статусу доступа (ТЗ 7.4). Пройденный курс сервер не предлагает. */
const COURSE_CTA: Record<CourseAccess, string> = {
  in_progress: 'growth.profession.courseContinue',
  purchased_not_started: 'growth.profession.courseStart',
  available: 'growth.profession.courseProgram',
};

/** 0072: порядок — сначала чего не хватает, выполненное последним и свёрнуто. */
const GROUPS: { status: RequirementStatus; titleKey: string }[] = [
  { status: 'missing', titleKey: 'growth.profession.groupMissing' },
  { status: 'improve', titleKey: 'growth.profession.groupImprove' },
  { status: 'meets', titleKey: 'growth.profession.groupMeets' },
];

export const GrowthProfession = memo(() => {
  const { t } = useTranslation();
  const { code = '' } = useParams<{ code: string }>();
  const { professions, loading } = useProfessions();
  const { ask, dialog } = useChooseGoalDialog();
  const { course } = useProfessionCourse(code);
  // Предпросмотр методиста выбирать цель не может: мутация откажет, а
  // кнопка, которую сервер откажет, хуже отсутствия кнопки.
  const { journey } = useCareerJourney();
  const isPreview = Boolean(journey?.isPreview);

  const profession = professions.find((row) => row.code === code);
  const [showMet, setShowMet] = useState(false);

  if (loading && !professions.length) {
    return (
      <PageWrapper>
        <Center>
          <Spin size="large" />
        </Center>
      </PageWrapper>
    );
  }

  if (!profession) {
    return (
      <PageWrapper>
        <CareerBackLink to={PATHS.careerProfessions} label={t('growth.profession.back')} />
        <Muted>{t('growth.profession.notFound')}</Muted>
      </PageWrapper>
    );
  }

  const { skillProgress: progress } = profession;
  const percent = progress.requiredCount
    ? Math.round((progress.meetsCount / progress.requiredCount) * 100)
    : 0;
  // 0073: две взаимоисключающие части — школа и самооценка.
  const sources = progressSources(progress);
  // 🔴 ТЗ 0071: группы размечает СЕРВЕР той же функцией, что считает число.
  //
  // 🔴 Дополнительные навыки в группы НЕ входят: в число над ними они не
  // входят тоже (иначе «3 z 18» стояло бы над «Spełniają wymagania · 6»).
  const required = profession.skills.filter((skill) => skill.priority !== 'optional');
  const optional = profession.skills.filter((skill) => skill.priority === 'optional');
  // 0072: первыми — пробелы; выполненное свёрнуто; пустые группы не выводятся.
  const grouped = GROUPS.map((group) => ({
    ...group,
    skills: required.filter((skill) => skill.requirementStatus === group.status),
  })).filter((group) => group.skills.length);
  const renderChip = (skill: IProfessionSkill) => (
    <SkillChip
      key={skill.id}
      // 🔴 ТЗ 0071: ссылка несёт ПРОФЕССИЮ. Без `track` карта открывалась по
      // цели студента, и из DevOps в Kubernetes человек попадал на чужую карту.
      to={skillMapHref({ track: profession.code, skill: skill.slug })}
      $status={skill.requirementStatus}>
      <SkillIcon
        slug={skill.slug}
        name={skill.name}
        category={skill.category}
        iconUrl={skill.iconUrl}
        size={18}
      />
      {skill.name}
      {chipLevel(t, skill) ? <ChipLevel>{chipLevel(t, skill)}</ChipLevel> : null}
    </SkillChip>
  );
  const mapHref = skillMapHref({ track: profession.code });

  return (
    <PageWrapper>
      <CareerBackLink to={PATHS.careerProfessions} label={t('growth.profession.back')} />

      <Head>
        <Title>
          {/* Тот же знак, что в списке профессий и на карточке трека. */}
          <TrackIcon icon={profession.icon} size={32} />
          {profession.title}
        </Title>
        {profession.summary ? <Lead>{profession.summary}</Lead> : null}

        {/* ТЗ 7.2: основное действие — выбрать цель, вторичное — карта. Просмотр
            чужой профессии цель не меняет: только явная кнопка с подтверждением. */}
        <Actions>
          {profession.isChosen ? (
            <>
              <GoalBadge>
                <CareerIcon name="goal" size={18} />
                {t('growth.profession.isGoal')}
              </GoalBadge>
              <PrimaryLink to={PATHS.careerCenter}>{t('growth.profession.openPlan')}</PrimaryLink>
            </>
          ) : !isPreview ? (
            <PrimaryButton
              type="button"
              onClick={() => ask({ code: profession.code, title: profession.title })}>
              {t('growth.profession.chooseGoal')}
            </PrimaryButton>
          ) : null}
          <SecondaryLink to={mapHref}>{t('growth.profession.seeMap')}</SecondaryLink>
        </Actions>
      </Head>

      {/* 0072: компактный показатель вместо большого круга — та же фраза, что
          на главной: «Spełniasz wymagania dla 10 z 16 wymaganych umiejętności». */}
      {profession.hasRequirements ? (
        <Fit aria-labelledby="profession-fit">
          <FitText id="profession-fit">
            {t('careerCenter.skillProgress.meets', {
              meets: progress.meetsCount,
              total: progress.requiredCount,
            })}
          </FitText>
          <Bar role="presentation">
            <BarFill style={{ width: `${percent}%` }} />
          </Bar>
          <Muted>
            {t('careerCenter.skillProgress.sources', {
              verified: sources.verified,
              self: sources.selfAssessed,
            })}
          </Muted>
        </Fit>
      ) : null}

      {profession.about ? <About>{profession.about}</About> : null}

      <Facts>
        {profession.salaryFrom && profession.salaryTo ? (
          <Fact>
            {t('growth.professions.salary', {
              from: profession.salaryFrom,
              to: profession.salaryTo,
              currency: profession.salaryCurrency,
            })}
          </Fact>
        ) : null}
        {/* «В нашей ленте», а не «на рынке». */}
        <Fact>{t('growth.professions.vacancies', { n: profession.liveVacancies })}</Fact>
      </Facts>

      {grouped.map((group) =>
        group.status === 'meets' ? (
          <Block key={group.status}>
            <DisclosureButton
              type="button"
              aria-expanded={showMet}
              aria-controls="profession-group-meets"
              onClick={() => setShowMet((value) => !value)}>
              <BlockTitle as="span">
                {t(group.titleKey)} · {group.skills.length}
              </BlockTitle>
              <Chevron $open={showMet} aria-hidden>
                <CareerIcon name="chevronDown" size={18} />
              </Chevron>
            </DisclosureButton>
            <Chips id="profession-group-meets" hidden={!showMet}>
              {group.skills.map(renderChip)}
            </Chips>
          </Block>
        ) : (
          <Block key={group.status}>
            <BlockTitle>
              {t(group.titleKey)} · {group.skills.length}
            </BlockTitle>
            <Chips>{group.skills.map(renderChip)}</Chips>
          </Block>
        ),
      )}

      {optional.length ? (
        <Block>
          <BlockTitle>
            {t('growth.profession.groupOptional')} · {optional.length}
          </BlockTitle>
          <Muted>{t('growth.profession.groupOptionalHint')}</Muted>
          <Chips>{optional.map(renderChip)}</Chips>
        </Block>
      ) : null}

      {/* ТЗ 0071, п. 7.4 / 0072, 3.5: ОДИН курс — какие пробелы закрывает,
          почему для этой профессии, есть ли доступ и куда ведёт кнопка. */}
      <Block aria-labelledby="profession-course-title">
        <BlockTitle id="profession-course-title">{t('growth.profession.courseTitle')}</BlockTitle>
        {course ? (
          <CareerLinkCard href={course.courseUrl}>
            <CourseName>{course.courseName}</CourseName>
            {course.closesSkillNames.length ? (
              <CourseLine>
                {t('growth.profession.courseCloses', {
                  skills: course.closesSkillNames.join(', '),
                })}
              </CourseLine>
            ) : null}
            <CourseWhy>
              {course.reasonCode === 'closes_critical'
                ? t('growth.profession.courseWhyCritical', { n: course.criticalGaps })
                : t('growth.profession.courseWhyImportant', { n: course.importantGaps })}
            </CourseWhy>
            <CourseWhy>
              {course.access === 'available'
                ? t('growth.profession.courseAccessPaid')
                : t('growth.profession.courseAccessOwned')}
            </CourseWhy>
            {course.durationLabel ? <CourseWhy>{course.durationLabel}</CourseWhy> : null}
            <CourseCta>{t(COURSE_CTA[course.access])}</CourseCta>
          </CareerLinkCard>
        ) : (
          <Muted>{t('growth.profession.courseNone')}</Muted>
        )}
      </Block>
      {dialog}
    </PageWrapper>
  );
});

/**
 * Уровень рядом с именем — только когда он что-то говорит (ТЗ 6.3). Слово
 * статуса уже стоит в заголовке группы, поэтому на чипе только числа.
 */
const chipLevel = (
  t: (key: string, vars?: Record<string, unknown>) => string,
  skill: IProfessionSkill,
) =>
  skill.requirementStatus === 'improve' && skill.myLevel !== null
    ? t('growth.profession.chipImprove', { mine: skill.myLevel, required: skill.requiredLevel })
    : skill.requirementStatus === 'missing'
    ? t('growth.profession.chipRequired', { required: skill.requiredLevel })
    : '';

const PageWrapper = styled.div`
  width: 100%;
  max-width: ${careerLayout.planWidth}px;
  padding: 8px 0 80px;
  display: flex;
  flex-direction: column;
  gap: ${careerLayout.sectionGap}px;
  color: ${careerColor.text};
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 0;
`;

const Head = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  ${careerType.h1()};
`;

/* 0072: вводный текст — обычным весом и в пределах 70 знаков строки. */
const Lead = styled.p`
  margin: 0;
  max-width: 70ch;
  color: ${careerColor.text2};
  ${careerType.body()};
`;

const About = styled.p`
  margin: 0;
  max-width: 70ch;
  ${careerType.body()};
`;

const Fit = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 560px;
`;

const FitText = styled.p`
  margin: 0;
  ${careerType.bodyStrong()};
`;

const Bar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: ${careerColor.line};
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: ${careerColor.done};
`;

const Facts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Fact = styled.span`
  padding: 6px 12px;
  border-radius: 999px;
  background: ${careerColor.hover};
  ${careerType.meta()};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const GoalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 12px;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${careerColor.active};
  ${careerType.meta()};
`;

const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 24px;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${Colors.main};
  color: ${CTA_TEXT};
  ${careerType.bodyStrong()};

  &:hover {
    color: ${CTA_TEXT};
    background: ${Colors.main_dark};
  }
`;

const PrimaryButton = styled.button`
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 24px;
  border: 0;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${Colors.main};
  color: ${CTA_TEXT};
  cursor: pointer;
  ${careerType.bodyStrong()};

  &:hover {
    background: ${Colors.main_dark};
  }
`;

const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border-radius: ${careerLayout.controlRadius}px;
  border: 1px solid ${careerColor.line};
  color: ${careerColor.text};
  ${careerType.body()};

  &:hover {
    color: ${careerColor.text};
    background: ${careerColor.hover};
  }
`;

const ChipLevel = styled.span`
  color: ${careerColor.text2};
`;

const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid ${careerColor.line};
`;

const BlockTitle = styled.h2`
  margin: 0;
  ${careerType.h2()};
`;

const DisclosureButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${careerColor.text};
  text-align: left;
  cursor: pointer;
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: ${careerColor.text2};
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/** Навык — ссылка на свой узел карты этой профессии. */
const SkillChip = styled(Link)<{ $status: RequirementStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid
    ${({ $status }) =>
      $status === 'meets'
        ? careerColor.done
        : $status === 'improve'
        ? careerColor.text
        : careerColor.line};
  color: ${careerColor.text};
  ${careerType.meta()};

  &:hover {
    color: ${careerColor.text};
    background: ${careerColor.hover};
  }
`;

const Muted = styled.span`
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const CourseName = styled.span`
  display: block;
  ${careerType.h3()};
`;

const CourseLine = styled.span`
  display: block;
  margin-top: 6px;
  ${careerType.body()};
`;

const CourseWhy = styled.span`
  display: block;
  margin-top: 4px;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const CourseCta = styled.span`
  display: inline-block;
  margin-top: 12px;
  text-decoration: underline;
  ${careerType.bodyStrong()};
`;
